/**
 * ProductsContext — global source of truth for the product catalog.
 *
 * Fetches from GET /api/products at boot. Falls back to the static list in
 * `../data/products` if the API is unreachable (so preview + first-load never
 * shows an empty catalog).
 */
import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { products as staticProducts } from "../data/products";

const ProductsContext = createContext(null);

export function ProductsProvider({ children }) {
  const [products, setProducts] = useState(staticProducts);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const apiBase = process.env.REACT_APP_BACKEND_URL || "";
      const res = await fetch(`${apiBase}/api/products`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (Array.isArray(data.items) && data.items.length > 0) {
        // Rewrite backend-relative image URLs (/api/product-images/xxx) to full URL
        const resolved = data.items.map((p) => ({
          ...p,
          image: p.image?.startsWith("/api/")
            ? `${apiBase}${p.image}`
            : p.image,
        }));
        setProducts(resolved);
      }
    } catch (err) {
      setError(err.message || "network");
      // keep static fallback
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const getProduct = useCallback(
    (slug) => products.find((p) => p.slug === slug),
    [products],
  );

  const value = useMemo(
    () => ({ products, loading, error, reload, getProduct }),
    [products, loading, error, reload, getProduct],
  );

  return <ProductsContext.Provider value={value}>{children}</ProductsContext.Provider>;
}

export function useProducts() {
  const ctx = useContext(ProductsContext);
  if (!ctx) throw new Error("useProducts must be used within ProductsProvider");
  return ctx;
}
